import { Segment } from '@dna/analytics'
import { client } from '@dna/constructor'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import * as paths from '@dna/paths'
import { storefrontClient } from '@dna/storefront'
import styles from '@dna/styles'
import {
  debounce,
  getBadgeMetaobject,
  getSectionPosition,
  igGetExperimentControl,
  removeTextBeforeCharacterAndTrim,
} from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  unsafeHTML,
  when,
} from '@lit'
import { Task } from '@lit/task'

// TODO: refactor to extract fetch logic and have this component focus exclusively on rendering the carousel from an array of items
class DNAProductCarousel extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    experimentationTrackingKey: {
      type: String,
      attribute: 'experimentation-tracking-key',
    },
    trackingKey: {
      type: String,
      attribute: 'tracking-key',
    },
    numberOfProducts: {
      type: Number,
      attribute: 'number-of-products',
    },
    items: {
      type: Array,
    },
    sectionSettings: {
      type: Array,
    },
    blockSettings: {
      type: Array,
    },
    customConstructorItemName: {
      type: String,
      attribute: 'custom-constructor-item-name',
    },
    activeSlide: {
      type: Number,
      state: true,
    },
    productAlignment: {
      type: String,
      attribute: 'product-alignment',
    },
    slidesPerPageOverride: {
      type: Function,
    },
    carouselStyle: {
      type: String,
      attribute: 'carousel-style',
    },
    imageLoading: {
      type: String,
      attribute: 'image-loading',
    },
    fetchPriority: {
      type: String,
      attribute: 'fetch-priority',
    },
    selectedTabIndex: {
      type: Number,
      state: true,
    },
    productId: {
      type: String,
      attribute: 'product-id',
    },
    tabClicked: {
      type: Boolean,
      state: true,
    },
  }

  $carousel = createRef()

  constructor() {
    super()
    this.items = []
    this.displayName = ''
    this.sectionSettings = []
    this.blockSettings = []
    this.carouselSlidePosition = 0
    this.slideChangedOnLoad = false
    this.activeSlide = 0
    this.productAlignment = 'center'
    this.selectedTabIndex = 1
    this.tabClicked = false
  }

  get maxItems() {
    return this.numberOfProducts || 20
  }

  get filteredItems() {
    let filtered = this.items ? this.items.filter((item) => item !== null) : []
    const hideOutOfStock = this.sectionSettings.hide_out_of_stock_products
    if (hideOutOfStock) {
      filtered = filtered.filter((item) => item.available)
    }
    return filtered.slice(0, this.maxItems)
  }

  get headlineAlignment() {
    return this.sectionSettings?.headline_alignment
  }

  get headline() {
    return this.sectionSettings?.headline
  }

  get subline() {
    return this.sectionSettings?.subline
  }

  get ctaText() {
    if (this.showTabs) {
      return this.blockSettings.find(
        (block) => block.index === this.selectedTabIndex,
      )?.cta_text
    }
    return this.sectionSettings?.cta_text
  }

  get ctaUrl() {
    if (this.showTabs) {
      return this.blockSettings.find(
        (block) => block.index === this.selectedTabIndex,
      )?.cta_url
    }
    return this.sectionSettings?.cta_url
  }

  get showSubline() {
    return !!subline || (!!ctaText && !!ctaUrl)
  }

  get showNavigation() {
    return this.filteredItems.length > 4 && !this.isMobile
  }

  get slidesPerPage() {
    if (this.slidesPerPageOverride) {
      return this.slidesPerPageOverride(this.viewport)
    }
    if (this.carouselStyle === 'compact') {
      if (this.viewport === 'widescreen') return 6.75
      if (this.viewport === 'desktop') return 6.75
      if (this.viewport === 'tablet') return 5.75
      return 2.5
    }
    if (this.viewport === 'widescreen') return 5.25
    if (this.viewport === 'desktop') return 5.25
    if (this.viewport === 'tablet') return 4.25
    return 1.75
  }

  get showTabs() {
    return this.blockSettings.length > 0
  }

  connectedCallback() {
    super.connectedCallback()

    this.$section = this.closest('.shopify-section')
    this.sectionPosition = getSectionPosition(this.$section)
    this.addEventListener(
      'sl-slide-change',
      debounce((e) => {
        this._handleSlideChangeOnLoad(e)
      }, 250),
    )
    this.slideChangeOnLoadHandled = false

    if (this.experimentationTrackingKey?.length) {
      this.$section.setAttribute(
        'data-experiment-selector',
        this.experimentationTrackingKey,
      )
    }
  }

  _podTask = new Task(this, {
    task: async ([selectedTabIndex]) => {
      const currentBlock = this.blockSettings.find(
        (block) => block.index === this.selectedTabIndex,
      )
      if (currentBlock?.manual_products.length > 0) {
        const manualItems = await storefrontClient.getProductsByBatch(
          currentBlock?.manual_products,
        )
        const badgeMetaobjects = await getBadgeMetaobject()
        if (badgeMetaobjects.length && manualItems.length) {
          for (const item of manualItems) {
            item.metafields.rothys.badges = badgeMetaobjects
              ?.filter((badge) =>
                item.metafields.rothys.badges?.includes(badge.id),
              )
              ?.map((badge) => {
                return {
                  text: badge.fields.text,
                  type: badge.fields.type,
                  start_date: badge.fields.start_date,
                  end_date: badge.fields.end_date,
                }
              })
          }
        }
        this.items = manualItems
      } else if (currentBlock?.pod_id) {
        const numResults = currentBlock.number_of_products
        const response = await client.getRecommendationPod(
          currentBlock.pod_id,
          {
            numResults: numResults,
            ...(this.productId && { itemIds: Number(this.productId) }),
          },
        )
        if (response.products.length > 0) {
          this.items = response.products
          this.dataset.cnstrcNumResults = numResults
          this.dataset.cnstrcRecommendationsPodId = currentBlock.pod_id
        }
      }
    },
    args: () => [this.selectedTabIndex],
  })

  _handleSlideChangeOnLoad = (event) => {
    // Prevent slide change events from firing Segment events when the carousel is loaded
    if (event.detail.index <= 0 && !this.slideChangeOnLoadHandled) {
      this.slideChangedOnLoad = true
      this.slideChangeOnLoadHandled = true
    }
    this.activeSlide = event.detail.index
    if (this.slideChangedOnLoad) {
      this.slideChangedOnLoad = false
    } else if (this.tabClicked) {
      this.tabClicked = false
    } else {
      this._trackCarouselScrolled(event)
    }
  }

  _handleTabClicked = (block) => {
    this.selectedTabIndex = block.index
    this.$carousel?.value?.goToSlide(0)
    this.tabClicked = true
    this._trackCarouselTabClicked(block)
  }

  _trackCarouselScrolled = (event) => {
    // @segment
    Segment.protect(() => {
      if (this.slideChangedOnLoad) {
        this.slideChangedOnLoad = false
      } else {
        const $carouselSectionTitle = event.target.renderRoot.querySelector(
          '.js-product-carousel-headline',
        )
        const scrollDirection =
          event.detail.index > this.carouselSlidePosition ? 'next' : 'previous'
        this.carouselSlidePosition = event.detail.index

        Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_SCROLLED, {
          product_carousel_section_title:
            $carouselSectionTitle?.textContent.trim(),
          product_carousel_scroll_direction: scrollDirection,
          product_carousel_pod_id: this.dataset.cnstrcRecommendationsPodId,
          section_page_position: this.sectionPosition,
          section_tracking_key: this.trackingKey,
          type: this.carouselStyle,
        })
      }
    })
  }

  _trackCarouselCTAClicked = (event) => {
    // @segment
    Segment.protect(() => {
      const $carouselSectionTitle = event.target
        .closest('dna-product-carousel')
        .renderRoot.querySelector('.js-product-carousel-headline')
      const $clickedProductCarouselCTA = event.target.closest('sl-button')

      Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CTA_CLICKED, {
        product_carousel_section_title:
          $carouselSectionTitle?.textContent.trim(),
        product_carousel_cta_link:
          $clickedProductCarouselCTA.getAttribute('href'),
        product_carousel_cta_text:
          $clickedProductCarouselCTA.textContent.trim(),
        product_carousel_pod_id: this.dataset.cnstrcRecommendationsPodId,
        section_page_position: this.sectionPosition,
        section_tracking_key: this.trackingKey,
      })
    })
  }

  _trackCarouselTabClicked = (block) => {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_TAB_CLICKED, {
        cta_text: block.tab_text,
        ...(block.pod_id && { product_carousel_pod_id: block.pod_id }),
        section_tracking_key: this.trackingKey,
      })
    })
  }

  _renderTabs() {
    return html`
      <div class="product-carousel__tabs product-carousel__tabs--${this.headlineAlignment} p1">
        ${repeat(
          this.blockSettings || [],
          (block) => block.tab_text,
          (block) => html`
          <div class="product-carousel__tabs--tab ${block.index === this.selectedTabIndex ? 'is-active' : ''}">
          <button @click=${() => this._handleTabClicked(block)} class="strong">
              ${block.tab_text}
          </button>
          </div>
          `,
        )}
      </div>
    `
  }

  _renderHeader() {
    const showSubline = !!this.subline || (!!this.ctaText && !!this.ctaUrl)

    return html`
      ${when(
        this.headline,
        () => html`
          <h2
            class="product-carousel__header-headline headline__${this.headlineAlignment} js-product-carousel-headline"
          >
            ${unsafeHTML(this.headline)}
          </h2>
        `,
        () => nothing,
      )}
      ${when(
        showSubline,
        () => html`
          <div class="product-carousel__header-subline subline__${this.headlineAlignment}">
            <div class="product-carousel__header-subtext">${this.subline}</div>
            ${when(
              !this.isMobile,
              () => html`
                <slot name="carousel-button" @click=${this._trackCarouselCTAClicked}></slot>
              `,
              () => nothing,
            )}
          </div>
        `,
        () => nothing,
      )}

    `
  }

  _renderCarouselEditorialImage(image) {
    return html`
      <sl-carousel-item>
        <dna-image
          class="product-carousel__editorial"
          src=${image.url}
          alt=${removeTextBeforeCharacterAndTrim(image?.alt, '|')}
          width="100%"
          height="100%"
          fit="cover"
          loading="eager"
          sizes="(max-width: 768px) 260px, (min-width: 768px) 600px"
        />
      </sl-carousel-item>
    `
  }

  render() {
    if (this.filteredItems.length === 0) return nothing

    const noSubline = !this.subline && this.ctaText && this.ctaUrl
    const showHeader =
      this.headline || this.subline || (this.ctaText && !!this.ctaUrl)

    const currentBlock = this.blockSettings.find(
      (block) => block.index === this.selectedTabIndex,
    )
    const editorialImage = currentBlock?.carousel_editorial_image?.url
      ? currentBlock.carousel_editorial_image
      : this.sectionSettings?.carousel_editorial_image?.url
        ? this.sectionSettings.carousel_editorial_image
        : null

    return html`
      ${when(
        showHeader,
        () => html`
          <div class="product-carousel__header product-carousel__header--${this.headlineAlignment} ${noSubline ? 'no-subline' : ''}">
            ${this._renderHeader()}
          </div>
        `,
        () => nothing,
      )}
      ${when(
        this.blockSettings.length > 1,
        () => html`
          ${this._renderTabs()}
        `,
        () => nothing,
      )}
      <div
        class=${classMap({
          'product-carousel__products': true,
          'product-carousel__products--center':
            !this.isMobile &&
            this.filteredItems.length < 4 &&
            this.productAlignment === 'center',
          'product-carousel__products--right':
            !this.isMobile &&
            this.filteredItems.length < 4 &&
            this.productAlignment === 'right',
        })}
      >
        <sl-carousel
          ${ref(this.$carousel)} 
          slides-per-page=${this.slidesPerPage}
          style="--aspect-ratio: unset; ${
            !this.isMobile ? '--slide-gap: 0px;' : nothing
          }"
          class=${!this.sectionSettings?.cta_url ? 'no-cta' : ''}
          ?navigation=${this.showNavigation}
          part="carousel"
          exportparts="carousel-item, product-card, scroll-container"
        >
          ${when(
            editorialImage,
            () => html`
                ${this._renderCarouselEditorialImage(editorialImage)}
             `,
            () => nothing,
          )}
          ${this.filteredItems.map((product) => {
            return html`
              <sl-carousel-item
                part="carousel-item"
                tabindex="0"
              >
                <dna-product-card
                  part="product-card"
                  class="product-card__product-carousel"
                  .product=${product}
                  tracking-key=${this.trackingKey}
                  section-position=${this.sectionPosition}
                  ?hide-product-color-count=${this.sectionSettings.hide_product_color_count}
                  ?hide-product-color-name=${this.sectionSettings.hide_product_color_name}
                  ?hide-badges=${this.sectionSettings.hide_badges}
                  ?hide-wishlist-icon=${this.sectionSettings.hide_wishlist_icon}
                  ?show-editorial-alt-image=${this.sectionSettings.show_editorial_alt_image}
                  ?show-review-stars=${this.sectionSettings.show_review_stars}
                  ?apply-test-1218=${this.applyTest1218}
                  enable-hover-image
                  data-cnstrc-item=${this.customConstructorItemName}
                  data-cnstrc-item-id=${product.id}
                  data-cnstrc-item-name=${product.title}
                  data-cnstrc-item-variation-id=${
                    product?.variants?.[0]?.id ?? nothing
                  }
                  data-cnstrc-strategy-id=${product?.strategy_id ?? nothing}
                  data-cnstrc-recommendations-pod-id=${this.dataset.cnstrcRecommendationsPodId ?? nothing}
                  carousel-style=${this.carouselStyle}
                  image-loading=${this.imageLoading}
                  fetch-priority=${this.fetchPriority}
                ></dna-product-card>
              </sl-carousel-item>
            `
          })}
          <span class="product-carousel__arrow product-carousel__arrow--previous" slot="previous-icon" ?hidden=${this.activeSlide === 0 || this.isMobile}>
            ${paths.chevronLeft}
          </span>
          <span class="product-carousel__arrow product-carousel__arrow--next " slot="next-icon" ?hidden=${this.activeSlide >= Math.ceil(this.filteredItems.length - this.slidesPerPage) || this.isMobile}>
            ${paths.chevronRight}
          </span>
        </sl-carousel>
      </div>
      ${when(
        this.ctaText && this.ctaUrl,
        () => html`
          <div class="product-carousel__button">
            <sl-button name="carousel-button" variant=${this.sectionSettings?.cta_style} href=${this.ctaUrl} @click=${this._trackCarouselCTAClicked}>${this.ctaText}</sl-button>
          </div>
        `,
        () => nothing,
      )}
    `
  }

  static styles = [
    styles.reset,
    styles.container,
    styles.typography,
    styles.shoelace,
    css`
      :host {
        position: relative;
      }
      @media screen and (min-width: 1024px) {
        .product-carousel__products {
          display: flex;
        }
      }
      @media screen and (min-width: 1024px) {
        :host sl-carousel {
          width: 100%;
        }
      }
      :host sl-carousel::part(scroll-container) {
        gap: 0;
        --aspect-ratio: 0;
      }
      .product-carousel__products--center sl-carousel::part(scroll-container) {
        justify-content: center;
      }
      .product-carousel__products--right sl-carousel::part(scroll-container) {
        justify-content: end;
      }
      :host sl-carousel::part(base) {
        grid-template-areas: 'slides' 'navigation';
        grid-template-columns: 1fr;
        gap: 0;
      }
      .product-carousel__arrow {
        color: var(--dna-color-midnight);
        background-color: red;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color:#FFFFFFB2;
      }

      .product-carousel__arrow--previous {
        padding-top: 7px;
        padding-right: 2px;
        margin-left: var(--dna-space-8);
      }

      .product-carousel__arrow--next {
        padding-top: 7px;
        padding-left: 2px;
        margin-right: var(--dna-space-8);
      }

      :host sl-carousel::part(navigation-button) {
        position: absolute;
        opacity: 0;
        transition: opacity 0.2s ease;
        border-radius: 50%;
        color: var(--dna-color-midnight);
      }

      :host sl-carousel:hover::part(navigation-button) {
        opacity: 1;
        pointer-events: auto;
      }

       :host sl-carousel::part(navigation-button--next) {
        right: 0;
      }
      :host sl-carousel-item {
        height: 100%;
        border-right: 1px solid var(--dna-brand-reverse);
      }
      .product-carousel__header {
        margin: var(--dna-space-4);
        margin-top: 0;
      }
      .product-carousel__tabs {
        margin-left: var(--dna-space-4);
        display: flex;
        gap: var(--dna-space-8);
      }
      .product-carousel__tabs--center {
        justify-content: center;
        margin-left: 0px;
      }
      .product-carousel__tabs--tab.is-active {
        border-bottom: var(--dna-space-1) solid var(--dna-color-reflex-blue);
      }
      .product-carousel__tabs button {
        text-transform: uppercase;
        margin-bottom: var(--dna-space-3);
        letter-spacing: 2px;
      }
      .product-carousel__header-headline {
        margin-top: 0;
        margin-bottom: 0;
        text-align: center;
        font: var(--dna-h3-font);
      }
      .headline__left {
        text-align: left;
      }
      .product-carousel__header-headline p {
        margin: 0;
      }
      .product-carousel__header-subline {
        display: flex;
        gap: var(--dna-space-3);
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      .product-carousel__editorial {
        width: 100%;
        height: 100%;
      }
      @media screen and (min-width: 768px) {
        .product-carousel__header-subline {
          gap: unset;
          flex-direction: row;
          justify-content: space-between;
        }
      }
      .product-carousel__header-subtext {
        text-align: center;
        margin-top: var(--dna-space-2);
        font: var(--dna-body-p2-font);
      }
      .product-carousel__button {
        text-align: center;
        margin-top: var(--dna-space-6);
      }
      @media screen and (max-width: 768px) {
        .product-carousel__header-headline {
          font: var(--dna-h3-font);
        }
        .subline__left {
          align-items: flex-start;
        }
        :host sl-carousel::part(scroll-container) {
          padding-left: 0;
        }
        :host sl-carousel.no-cta::part(navigation) {
          position: relative;
          margin-top: 0px;
          margin-bottom: 52px;
        }
        :host sl-carousel.no-cta::part(navigation-button--previous) {
          position: absolute;
          top: 0;
          right: var(--dna-space-20);
       }
        :host sl-carousel.no-cta::part(navigation-button--next) {
          position: absolute;
          top: 0;
          right: var(--dna-space-4);
        }
      }
      @media screen and (min-width: 1024px) {
        .product-carousel__header {
          margin: var(--dna-space-6) var(--dna-space-10);
          margin-top: 0;
        }
        .product-carousel__header.no-subline {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .product-carousel__header.product-carousel__header--center {
          justify-content: center;
        }
        .product-carousel__header-subtext {
          font: var(--dna-body-p1-font);
          text-align: left;
        }
        .product-carousel__header-subline.subline__center {
          justify-content: center;
        }
        .product-carousel__tabs {
          margin-left: var(--dna-space-10);
          gap: 56px;
        }
        .product-carousel__tabs--center {
          margin-left: 0px;
        }
      }
    `,
  ]
}

customElements.define('dna-product-carousel', DNAProductCarousel)
